import {
  CalculateTermPlanOptionsDto,
  CalculateTermPlanOptionsSimulationDto,
  TermPlanOptionDto,
} from '@src/data/types/TermPlanOptionDto'
import { getApiClient } from '@src/services/api-client'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'

const SCOPE = 'termPlanOptions'

export type TermPlanOptionsQueryInput =
  | { isSimulation: false; dto: CalculateTermPlanOptionsDto }
  | { isSimulation: true; dto: CalculateTermPlanOptionsSimulationDto }

export const termPlanQueryKeys = {
  all: [SCOPE] as const,
  options: (financingProgramId: string, input: TermPlanOptionsQueryInput | undefined) =>
    [SCOPE, financingProgramId, input] as const,
}

const calculateTermPlanOptions = async (
  financingProgramId: string,
  dto: CalculateTermPlanOptionsDto,
): Promise<TermPlanOptionDto[]> => {
  const response = await getApiClient().post<TermPlanOptionDto[]>(
    `/Applications/${financingProgramId}/CalculateTermAndPlanOptions`,
    dto,
  )
  return response.data
}

const calculateTermPlanOptionsSimulation = async (
  financingProgramId: string,
  dto: CalculateTermPlanOptionsSimulationDto,
): Promise<TermPlanOptionDto[]> => {
  const response = await getApiClient().post<TermPlanOptionDto[]>(
    `/Applications/${financingProgramId}/CalculateTermAndPlanOptions/Simulation`,
    dto,
  )
  return response.data
}

export const useCalculateTermPlanOptions = (
  financingProgramId: string,
  input: TermPlanOptionsQueryInput | undefined,
): [TermPlanOptionDto[] | null, boolean, AxiosError | null] => {
  const { data, isFetching, error } = useQuery<TermPlanOptionDto[], AxiosError>({
    queryKey: termPlanQueryKeys.options(financingProgramId, input),
    queryFn: () =>
      input!.isSimulation
        ? calculateTermPlanOptionsSimulation(financingProgramId, input!.dto)
        : calculateTermPlanOptions(financingProgramId, input!.dto),
    enabled: !!financingProgramId && !!input,
  })
  return [data ?? null, isFetching, error]
}
